import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  dateInfo,
  eventInfo,
  phraseInfo,
  sentenceInfo,
  tableInfo,
} from '../DocumentView/State/documentState';
import {
  fetchClauseDataByType,
  fetchCurrencyList,
  fetchInsightsSection,
  getExtractedData,
} from '../Services/InsightAPI';

const initialState = {
  contract: null,
  sentenceData: null,
  sentenceLoader: false,
  componentType: '',
  editOptionSelected: false,
  phraseEditOptionSelected: false,
  phraseDeleteStatus: false,
  phraseAddStatus: false,
  eventEditingStatus: false,
  dateEditingStatus: false,
  currencyList: [],
  insightsSection: null,
  sectionLoading: false,
  clauseData: {
    clauses: {},
    loading: false,
  },
  dataPointName: '',
  highlightedId: null,
  savedHighlightedSentences: null,
  savedHighlightedPhrases: null,
  savedHighlightedTableCells: null,
} as {
  contract: any | null;
  sentenceData: any;
  sentenceLoader: boolean;
  componentType: string;
  editOptionSelected: boolean;
  highlightedId?: number[] | null;
  savedHighlightedSentences?: any[] | null;
  savedHighlightedPhrases?: any[] | null;
  phraseEditOptionSelected: boolean;
  phraseDeleteStatus: boolean;
  phraseInDeleteMode?: phraseInfo | null;
  phraseAddStatus: boolean;
  phraseInAddMode?: phraseInfo | null;
  savedHighlightedTableCells?: tableInfo[] | null;
  savedHighlightedDates?: dateInfo[] | null;
  dateInAddEditMode?: dateInfo | null;
  dateEditingStatus: boolean;
  savedHighlightedEvents?: eventInfo[] | null;
  eventInAddEdit?: eventInfo | null;
  eventEditingStatus: boolean;
  currencyList: any;
  insightsSection: any;
  sectionLoading: boolean;
  clauseData: any;
  dataPointName: string;
};

const insightSlice = createSlice({
  name: 'insight',
  initialState,
  reducers: {
    saveHighlightedComponentType: (
      state,
      {
        payload,
      }: PayloadAction<{
        componentType: string;
        editOptionSelected: boolean;
      }>
    ) => {
      state.componentType = payload.componentType;
      state.editOptionSelected = payload.editOptionSelected;
    },
    saveHighlightedId: (
      state,
      {
        payload,
      }: PayloadAction<{
        highlightedId?: number[] | null;
      }>
    ) => {
      state.highlightedId = payload.highlightedId;
    },
    saveHighlightedSentences: (
      state,
      {
        payload,
      }: PayloadAction<{
        savedHighlightedSentences?: sentenceInfo[] | null;
      }>
    ) => {
      state.savedHighlightedSentences = payload.savedHighlightedSentences;
    },
    saveHighlightedPhrases: (
      state,
      {
        payload,
      }: PayloadAction<{
        savedHighlightedPhrases?: phraseInfo[] | null;
      }>
    ) => {
      state.savedHighlightedPhrases = payload.savedHighlightedPhrases;
    },
    savePhraseEditOption: (
      state,
      {
        payload,
      }: PayloadAction<{
        phraseEditOptionSelected: boolean;
      }>
    ) => {
      state.phraseEditOptionSelected = payload.phraseEditOptionSelected;
    },
    saveDeletePhrase: (
      state,
      {
        payload,
      }: PayloadAction<{
        phraseDeleteStatus: boolean;
        phraseInDeleteMode?: phraseInfo | null;
      }>
    ) => {
      state.phraseDeleteStatus = payload.phraseDeleteStatus;
      state.phraseInDeleteMode = payload.phraseInDeleteMode;
    },
    saveAddPhrase: (
      state,
      {
        payload,
      }: PayloadAction<{
        phraseAddStatus: boolean;
        phraseInAddMode?: phraseInfo | null;
      }>
    ) => {
      state.phraseAddStatus = payload.phraseAddStatus;
      state.phraseInAddMode = payload.phraseInAddMode;
    },
    saveHighlightedTableCells: (
      state,
      {
        payload,
      }: PayloadAction<{
        savedHighlightedTableCells?: tableInfo[] | null;
      }>
    ) => {
      state.savedHighlightedTableCells = payload.savedHighlightedTableCells;
    },
    saveHighlightedDates: (
      state,
      {
        payload,
      }: PayloadAction<{
        savedHighlightedDates?: dateInfo[] | null;
      }>
    ) => {
      state.savedHighlightedDates = payload.savedHighlightedDates;
    },
    saveDateInAddEditMode: (
      state,
      {
        payload,
      }: PayloadAction<{
        dateInAddEditMode?: dateInfo | null;
      }>
    ) => {
      state.dateInAddEditMode = payload.dateInAddEditMode;
    },
    saveDateEditingStatus: (
      state,
      {
        payload,
      }: PayloadAction<{
        dateEditingStatus: boolean;
      }>
    ) => {
      state.dateEditingStatus = payload.dateEditingStatus;
    },
    saveHighlightedEvents: (
      state,
      {
        payload,
      }: PayloadAction<{
        savedHighlightedEvents?: eventInfo[] | null;
      }>
    ) => {
      state.savedHighlightedEvents = payload.savedHighlightedEvents;
    },
    saveEventInAddEdit: (
      state,
      {
        payload,
      }: PayloadAction<{
        eventInAddEdit?: eventInfo;
      }>
    ) => {
      state.eventInAddEdit = payload.eventInAddEdit;
    },
    setDataPointName: (
      state,
      {
        payload,
      }: PayloadAction<{ dataPointName: string; editOptionSelected: boolean }>
    ) => {
      state.dataPointName = payload.dataPointName;
      state.editOptionSelected = payload.editOptionSelected;
    },
    saveEventEditingStatus: (
      state,
      { payload }: PayloadAction<{ eventEditingStatus: boolean }>
    ) => {
      state.eventEditingStatus = payload.eventEditingStatus;
    },
    setClauseLoading: (state, { payload }) => {
      state.clauseData.clauses = { ...state.clauseData.clauses, ...payload };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getExtractedData.pending, (state) => {
        state.sentenceLoader = true;
      })
      .addCase(getExtractedData.fulfilled, (state, { payload }) => {
        state.sentenceData = payload;
        state.sentenceLoader = false;
      })
      .addCase(fetchInsightsSection.pending, (state) => {
        state.sectionLoading = true;
      })
      .addCase(fetchInsightsSection.fulfilled, (state, { payload }) => {
        state.insightsSection = payload;
        state.sectionLoading = false;
      })
      .addCase(fetchClauseDataByType.pending, (state) => {
        // state.clauseData.loading = true;
      })
      .addCase(fetchClauseDataByType.fulfilled, (state, { payload }) => {
        const clauseType = Object.keys(payload)[0];

        const incoming = payload[clauseType];
        const existing = state.clauseData.clauses[clauseType];

        state.clauseData.clauses[clauseType] = {
          ...existing,
          ...incoming,

          // preserve edited_content if API sends null
          edited_content: incoming.edited_content ?? existing?.edited_content,

          // preserve raw_content if needed
          raw_content: incoming.raw_content ?? existing?.raw_content,

          // changeList should always be updated
          changeList: incoming.changeList,
        };
      })
      .addCase(fetchCurrencyList.fulfilled, (state, { payload }: any) => {
        state.currencyList = payload.results;
      });
  },
});

export const {
  saveHighlightedComponentType,
  saveAddPhrase,
  saveDeletePhrase,
  saveEventInAddEdit,
  saveHighlightedEvents,
  saveHighlightedId,
  saveHighlightedPhrases,
  saveHighlightedSentences,
  saveHighlightedTableCells,
  savePhraseEditOption,
  saveHighlightedDates,
  saveDateEditingStatus,
  saveDateInAddEditMode,
  saveEventEditingStatus,
  setDataPointName,
  setClauseLoading,
} = insightSlice.actions;

export default insightSlice.reducer;
